import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import api, { API } from '@/lib/api';
import { openPrintWindow } from '@/pages/owner/OwnerPrintTemplates';

/**
 * Custom hook untuk logic cetak batch dokumen.
 * Meng-encapsulate:
 *   - Fetch PDF / Dot Matrix dari backend
 *   - Handle error 400 dengan missing_fields (untuk popup)
 *   - Support mode preview (return blob/text ke caller) vs direct print
 *   - Save info pengiriman ke batch
 *
 * Return: { doPrintPdf, doPrintDotMatrix, saveDeliveryInfo, printingIdx }
 */
export default function usePrintBatch({ tx, docType, previewMode, onPreview, onMissingInfo, docInfo }) {
  const [printingIdx, setPrintingIdx] = useState(null);

  const _parseError = async (res) => {
    let msg = 'Gagal cetak';
    let missing = null;
    try {
      const err = await res.json();
      const d = err.detail;
      if (typeof d === 'string') msg = d;
      else if (d?.message) { msg = d.message; missing = d.missing_fields || null; }
    } catch (_) { /* body bukan JSON */ }
    return { msg, missing };
  };

  const doPrintPdf = useCallback(async (batchIndex, batchesRef) => {
    setPrintingIdx(batchIndex);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/transactions/${tx.id}/deliveries/${batchIndex}/document/${docType}.pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const { msg, missing } = await _parseError(res);
        if (res.status === 400 && missing?.length) {
          const b = (batchesRef || []).find((x) => x.batch_index === batchIndex);
          if (b && onMissingInfo) { onMissingInfo({ batch: b, missing }); return; }
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const filename = `${docInfo.filenamePrefix}-${tx.invoice_no}-B${batchIndex}.pdf`;
      if (previewMode && onPreview) {
        onPreview({
          mode: 'pdf',
          pdfBlob: blob,
          filename,
          title: `Pratinjau ${docInfo.label} — ${tx.invoice_no} Batch #${batchIndex}`,
        });
        return;
      }
      // Direct download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${docInfo.label} batch #${batchIndex} berhasil dicetak`);
    } catch (e) {
      console.error('[usePrintBatch] gagal cetak PDF:', e);
      toast.error(e.message || 'Gagal cetak');
    } finally { setPrintingIdx(null); }
  }, [tx, docType, previewMode, onPreview, onMissingInfo, docInfo]);

  const doPrintDotMatrix = useCallback(async (batchIndex, batchesRef) => {
    setPrintingIdx(batchIndex);
    try {
      const token = localStorage.getItem('token');
      const dmType = docType === 'delivery-order' ? 'delivery_order' : docType;
      const res = await fetch(
        `${API}/transactions/${tx.id}/print/${dmType}?batch=${batchIndex}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const { msg, missing } = await _parseError(res);
        if (res.status === 400 && missing?.length) {
          const b = (batchesRef || []).find((x) => x.batch_index === batchIndex);
          if (b && onMissingInfo) { onMissingInfo({ batch: b, missing }); return; }
        }
        throw new Error(msg);
      }
      const text = await res.text();
      const orientation = res.headers.get('X-Print-Orientation') || 'portrait';
      const winTitle = `${docInfo.filenamePrefix} ${tx.invoice_no} Batch ${batchIndex}`;
      if (previewMode && onPreview) {
        onPreview({
          mode: 'dotmatrix',
          dmText: text,
          dmOrientation: orientation,
          filename: winTitle,
          title: `Pratinjau ${docInfo.label} — ${tx.invoice_no} Batch #${batchIndex}`,
        });
        return;
      }
      openPrintWindow(text, winTitle, orientation);
      toast.success(`${docInfo.label} batch #${batchIndex} siap dicetak (Dot Matrix)`);
    } catch (e) {
      console.error('[usePrintBatch] gagal cetak dot matrix:', e);
      toast.error(e.message || 'Gagal cetak dot matrix');
    } finally { setPrintingIdx(null); }
  }, [tx, docType, previewMode, onPreview, onMissingInfo, docInfo]);

  const saveDeliveryInfo = useCallback(async (batchIndex, fields) => {
    return api.patch(`/transactions/${tx.id}/deliveries/${batchIndex}`, {
      driver_name: fields.driver || null,
      vehicle_plate: fields.plate || null,
      delivery_address: fields.address || null,
      delivery_date: fields.deliveryDate || null,
    });
  }, [tx]);

  return { doPrintPdf, doPrintDotMatrix, saveDeliveryInfo, printingIdx };
}
